import { useMemo, useState } from 'react';
import {
  Zap, FileCode2, Copy, Download, Plus, Trash2, Check, AlertTriangle,
  Layers, CreditCard, Wallet, ShieldAlert, ChevronRight,
} from 'lucide-react';
import {
  RECIPES, RECIPE_GROUPS, getRecipe, initialValues, validateRecipe,
  fieldVisible, buildBatch,
} from '../../data/accRecipes';

const GROUP_META = {
  'Sub_Program & Purse': { icon: Wallet, color: 'emerald' },
  'Fees, MCCs & Countries': { icon: ShieldAlert, color: 'amber' },
  'Packages & BPA': { icon: CreditCard, color: 'blue' },
};

const COLOR = {
  emerald: { chip: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'text-emerald-500', ring: 'hover:border-emerald-300' },
  amber: { chip: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'text-amber-500', ring: 'hover:border-amber-300' },
  blue: { chip: 'bg-blue-50 text-blue-700 border-blue-200', dot: 'text-blue-500', ring: 'hover:border-blue-300' },
};

// Lightweight XML syntax highlight for the preview pane.
function HighlightedXml({ xml }) {
  const html = useMemo(() => {
    const esc = xml
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    return esc
      .replace(/(&lt;\/?)([\w_]+)/g, '$1<span class="text-sky-400">$2</span>')
      .replace(/([\w]+)=(&quot;[^&]*&quot;|"[^"]*")/g, '<span class="text-fuchsia-300">$1</span>=<span class="text-lime-300">$2</span>')
      .replace(/(&gt;)([^&<]+)(&lt;)/g, '$1<span class="text-amber-200">$2</span>$3');
  }, [xml]);
  return (
    <pre
      className="text-[12px] leading-relaxed font-mono whitespace-pre overflow-auto p-4 text-slate-300"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function Field({ field, value, error, onChange }) {
  const base =
    'w-full rounded-lg border px-3 py-2 text-sm bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 ' +
    (error ? 'border-red-400 ring-1 ring-red-200' : 'border-gray-300 focus:border-emerald-400');
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1">
        {field.label}
        {field.help && <span className="ml-2 font-normal text-gray-400">{field.help}</span>}
      </label>
      {field.type === 'select' ? (
        <select className={base} value={value} onChange={(e) => onChange(e.target.value)}>
          {field.options.map((o) => (
            <option key={o} value={o}>{o === '' ? '— none —' : o}</option>
          ))}
        </select>
      ) : (
        <input
          className={base}
          type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
          value={value}
          step={field.type === 'number' ? 'any' : undefined}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {error && <p className="text-[11px] text-red-500 mt-1">{error}</p>}
    </div>
  );
}

export default function AccConfigBuilder() {
  const [businessPartner, setBusinessPartner] = useState('Sunny Benefits');
  const [filename, setFilename] = useState('sunny_acc_config');
  const [selectedId, setSelectedId] = useState('purse-expiry');
  const [values, setValues] = useState(() => initialValues(getRecipe('purse-expiry')));
  const [cart, setCart] = useState([]); // [{ id, title, item }]
  const [copied, setCopied] = useState(false);

  const recipe = getRecipe(selectedId);
  const errors = useMemo(() => validateRecipe(recipe, values), [recipe, values]);
  const isValid = Object.keys(errors).length === 0;
  const meta = GROUP_META[recipe.group];
  const colors = COLOR[meta.color];

  const currentItem = useMemo(() => recipe.build(values), [recipe, values]);

  // Live preview: cart contents + the in-progress recipe.
  const previewXml = useMemo(() => {
    const items = [...cart.map((c) => c.item), currentItem];
    return buildBatch(businessPartner, items);
  }, [cart, currentItem, businessPartner]);

  const cartOnlyXml = useMemo(
    () => buildBatch(businessPartner, cart.map((c) => c.item)),
    [cart, businessPartner],
  );

  function pickRecipe(id) {
    setSelectedId(id);
    setValues(initialValues(getRecipe(id)));
  }

  function setVal(fid, v) {
    setValues((prev) => ({ ...prev, [fid]: v }));
  }

  function addToBatch() {
    if (!isValid) return;
    setCart((prev) => [...prev, { id: recipe.id + ':' + prev.length, title: recipe.title, item: currentItem }]);
    setValues(initialValues(recipe)); // reset for next entry
  }

  function removeFromCart(id) {
    setCart((prev) => prev.filter((c) => c.id !== id));
  }

  const exportXml = cart.length ? cartOnlyXml : previewXml;
  const safeName = (filename || 'acc_config').replace(/[^A-Za-z0-9_]/g, '_');

  function copyXml() {
    navigator.clipboard?.writeText(exportXml).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  function downloadXml() {
    const blob = new Blob([exportXml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${safeName}.XML`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Zap className="w-6 h-6 text-emerald-500" />
            ACC Config Builder
          </h2>
          <p className="text-gray-500 mt-1 text-sm">
            FIS Prepaid ACC · Quick Edit — build surgical configuration changes and export batch-ready XML.
          </p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Business Partner</label>
            <input
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400/40"
              value={businessPartner}
              onChange={(e) => setBusinessPartner(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">File name</label>
            <div className="flex items-center rounded-lg border border-gray-300 overflow-hidden">
              <input
                className="px-3 py-2 text-sm focus:outline-none w-40"
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
              />
              <span className="px-2 text-xs text-gray-400 bg-gray-50 border-l border-gray-200 py-2">.XML</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recipe gallery */}
      {RECIPE_GROUPS.map((group) => {
        const gm = GROUP_META[group];
        const gc = COLOR[gm.color];
        const Icon = gm.icon;
        return (
          <section key={group}>
            <div className="flex items-center gap-2 mb-3">
              <Icon className={`w-4 h-4 ${gc.dot}`} />
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500">{group}</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {RECIPES.filter((r) => r.group === group).map((r) => {
                const on = r.id === selectedId;
                return (
                  <button
                    key={r.id}
                    onClick={() => pickRecipe(r.id)}
                    className={`text-left rounded-xl border p-3 transition bg-white ${
                      on ? `border-2 ${gc.dot.replace('text', 'border')} shadow-sm` : `border-gray-200 ${gc.ring}`
                    }`}
                  >
                    <div className="text-[10px] font-mono text-gray-400 truncate">{r.nodeLabel}</div>
                    <div className="font-semibold text-sm text-gray-900 mt-1">{r.title}</div>
                    <div className="text-xs text-gray-500 mt-1 leading-snug">{r.blurb}</div>
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}

      {/* Editor + preview */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Form */}
        <div className="rounded-xl border border-gray-200 bg-white">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${colors.chip}`}>
                {recipe.group}
              </span>
              <span className="font-semibold text-gray-900 text-sm">{recipe.title}</span>
            </div>
            <span className="text-[11px] font-mono text-gray-400">{recipe.nodeLabel}</span>
          </div>
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {recipe.fields.filter((f) => fieldVisible(f, values)).map((f) => (
              <Field key={f.id} field={f} value={values[f.id] ?? ''} error={errors[f.id]} onChange={(v) => setVal(f.id, v)} />
            ))}
          </div>
          <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between bg-gray-50/60 rounded-b-xl">
            <div className="text-xs">
              {isValid ? (
                <span className="text-emerald-600 inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Valid &amp; ready</span>
              ) : (
                <span className="text-red-500 inline-flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> {Object.keys(errors).length} issue{Object.keys(errors).length > 1 ? 's' : ''} to fix
                </span>
              )}
            </div>
            <button
              onClick={addToBatch}
              disabled={!isValid}
              className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-lg px-3 py-2 bg-emerald-500 text-white disabled:opacity-40 hover:bg-emerald-600 transition"
            >
              <Plus className="w-4 h-4" /> Add to batch
            </button>
          </div>
        </div>

        {/* XML preview */}
        <div className="rounded-xl border border-gray-200 bg-slate-900 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-700/60">
            <div className="flex items-center gap-2 text-slate-300 text-xs font-semibold">
              <FileCode2 className="w-4 h-4" /> Live XML
              <span className="text-slate-500 font-normal">
                {new Blob([exportXml]).size} bytes · {cart.length + 1} node{cart.length ? 's' : ''} previewed
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={copyXml} className="inline-flex items-center gap-1 text-xs text-slate-300 hover:text-white px-2 py-1 rounded border border-slate-700">
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />} {copied ? 'Copied' : 'Copy'}
              </button>
              <button onClick={downloadXml} className="inline-flex items-center gap-1 text-xs font-semibold bg-emerald-500 text-white px-2.5 py-1 rounded hover:bg-emerald-600">
                <Download className="w-3.5 h-3.5" /> {safeName}.XML
              </button>
            </div>
          </div>
          <div className="flex-1 min-h-[320px] max-h-[460px] overflow-auto">
            <HighlightedXml xml={previewXml} />
          </div>
        </div>
      </section>

      {/* Batch cart */}
      <section className="rounded-xl border border-gray-200 bg-white">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-gray-500" />
            <h3 className="text-sm font-semibold text-gray-900">Batch contents</h3>
            <span className="text-xs text-gray-400">{cart.length} node{cart.length === 1 ? '' : 's'} staged</span>
          </div>
          {cart.length > 0 && (
            <button onClick={() => setCart([])} className="text-xs text-gray-500 hover:text-red-500 inline-flex items-center gap-1">
              <Trash2 className="w-3.5 h-3.5" /> Clear all
            </button>
          )}
        </div>
        {cart.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-gray-400">
            Nothing staged yet. Fill a recipe above and <span className="font-semibold text-gray-500">Add to batch</span> to combine
            multiple changes into one file — they’ll be exported together.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {cart.map((c, i) => (
              <li key={c.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                <span className="text-xs font-mono text-gray-400 w-6">{i + 1}.</span>
                <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
                <span className="font-medium text-gray-800">{c.title}</span>
                <span className="text-xs font-mono text-gray-400">&lt;{c.item.node}&gt;</span>
                <button onClick={() => removeFromCart(c.id)} className="ml-auto text-gray-400 hover:text-red-500">
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Guardrail note */}
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800 flex gap-2">
        <ShieldAlert className="w-4 h-4 flex-none mt-0.5" />
        <p>
          Inputs reject the characters <code className="font-mono">&amp; &lt; &gt; &quot; # &apos;</code> (they abort the loader),
          enforce <code className="font-mono">PurseName</code> as <code className="font-mono">[A-Z]&#123;3&#125;[0-9]&#123;4&#125;</code>,
          and validate ranges/dates per the v1.26 spec. File names are sanitized to alphanumeric + underscore with a <code className="font-mono">.XML</code> suffix.
          Drop the exported file on the MoveIT landing zone; FIS returns <code className="font-mono">Log.</code>/<code className="font-mono">Error.</code> files.
        </p>
      </div>
    </div>
  );
}
